import { useState, useEffect } from 'react';
import { useAuth } from '@/providers/AuthProvider';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowLeft, Wallet, ChevronRight, HelpCircle, Zap } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface RideDriverWalletProps {
  onBack: () => void;
}

const RideDriverWallet = ({ onBack }: RideDriverWalletProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [balance, setBalance] = useState(0);
  const [showDeposit, setShowDeposit] = useState(false);
  const [depositAmount, setDepositAmount] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user) return;
    const fetchBalance = async () => {
      const { data } = await supabase
        .from('ride_driver_profiles')
        .select('total_earnings')
        .eq('user_id', user.id)
        .single();
      setBalance(data?.total_earnings || 0);
    };
    fetchBalance();
  }, [user]);

  const handleDeposit = async () => {
    const amount = parseFloat(depositAmount);
    if (!amount || amount <= 0) {
      toast({ title: 'Invalid amount', description: 'Please enter a valid amount.' });
      return;
    }
    setLoading(true);
    // For now, just show success - would integrate with payment gateway
    toast({ title: 'Deposit initiated', description: `D${amount.toFixed(2)} deposit is being processed.` });
    setShowDeposit(false);
    setDepositAmount('');
    setLoading(false);
  };

  return (
    <div className="p-6">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" onClick={onBack}><ArrowLeft className="w-5 h-5" /></Button>
        <h2 className="text-2xl font-bold">Wallet</h2>
      </div>

      {/* Balance card */}
      <div className="bg-accent rounded-2xl p-6 mb-6">
        <p className="text-sm text-muted-foreground mb-1">Balance</p>
        <div className="flex items-center justify-between">
          <p className="text-3xl font-bold">D{balance.toFixed(2)}</p>
          <ChevronRight className="w-6 h-6 text-muted-foreground" />
        </div>

        <button
          onClick={() => setShowDeposit(!showDeposit)}
          className="mt-4 flex items-center gap-2 bg-background rounded-full px-4 py-2 text-sm font-medium hover:bg-background/80 transition-colors"
        >
          <Zap className="w-4 h-4" />
          Deposit
        </button>
      </div>

      {showDeposit && (
        <div className="bg-accent/50 rounded-xl p-4 mb-6 space-y-3">
          <p className="font-semibold">Deposit to GAMBGO Account</p>
          <Input
            type="number"
            placeholder="Enter amount (D)"
            value={depositAmount}
            onChange={(e) => setDepositAmount(e.target.value)}
            min="1"
          />
          <Button onClick={handleDeposit} disabled={loading} className="w-full bg-foreground text-background hover:bg-foreground/90">
            {loading ? 'Processing...' : 'Confirm Deposit'}
          </Button>
        </div>
      )}

      {/* Menu items */}
      <div className="space-y-0">
        <button className="w-full flex items-center gap-4 py-4 border-t border-border text-left">
          <Wallet className="w-5 h-5" />
          <span className="font-medium">Payment Methods</span>
        </button>
        <button className="w-full flex items-center gap-4 py-4 border-t border-b border-border text-left">
          <HelpCircle className="w-5 h-5" />
          <span className="font-medium">Help</span>
        </button>
      </div>
    </div>
  );
};

export default RideDriverWallet;
